import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  isConnected: false,
});

export const useWebSocket = () => useContext(WebSocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const businessId = user?.businessId;

  useEffect(() => {
    // Only connect if the user is authenticated and has a businessId
    if (!businessId) {
      return;
    }

    // Connect to the backend
    const wsUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:3001';
    
    const newSocket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnectionDelayMax: 10000,
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      newSocket.emit('join_business', businessId);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [businessId]);

  const value = useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
